import { Core } from "@faire/design-tokens";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import styled, { css } from "styled-components";

interface ICustomAttrs {
  $expanded?: boolean;
  $mini?: boolean;
  $miniInputSize?: number;
  $placeholder?: string;
  $isLoading?: boolean;
}

const InputScrollOffset = 51;
const SearchResultMaxHeight = 420;

export const scrollToTopIfNeeded = (
  itemRef: HTMLLIElement,
  listRef: HTMLUListElement
) => {
  const itemTopPosition = itemRef.offsetTop - InputScrollOffset;
  const itemBottomPosition = itemTopPosition + itemRef.clientHeight;
  const minVisibleOffset = listRef.scrollTop;
  const maxVisibleOffset = minVisibleOffset + SearchResultMaxHeight;
  const isScrollNeeded =
    maxVisibleOffset < itemBottomPosition || minVisibleOffset > itemTopPosition;
  if (isScrollNeeded) {
    listRef.scrollTo?.(0, itemTopPosition);
  }
};

export const Wrapper = styled.section<ICustomAttrs>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  background-color: ${Core.surface.inverse};

  height: ${({ $mini: mini }) =>
    mini
      ? "40px"
      : css`
          ${InputScrollOffset}px
        `};
  margin: ${({ $mini: mini }) => (mini ? "17px 20px 0 auto" : "0 auto")};

  width: ${({
    $mini: mini,
    $expanded: expanded,
    $miniInputSize: miniInputSize,
  }) => {
    if (mini) {
      return css`
        ${miniInputSize || 340}px
      `;
    }
    return expanded ? "100%" : "648px";
  }};
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

export const StyledLoadingSpinner = styled(LoadingSpinner)`
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  margin: auto;
  z-index: 1;
`;

const inputStyle = (mini: boolean) => `
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: 1px solid ${Core.border.muted};
  border-left: none;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  line-height: 2;
  height: 100%;
  display: block;
  ${
    mini
      ? `
    width: -webkit-calc(100% - 40px);
    width: -moz-calc(100% - 40px);
    width: calc(100% - 40px);
  `
      : `
    width: -webkit-calc(100% - 51px);
    width: -moz-calc(100% - 51px);
    width: calc(100% - 51px);
  `
  }
  ${TypographyStyles({})}
  &.placeholder-search-help-center-focused {
    &::placeholder {
      color: ${Core.text.subdued};
    }
  }
  &::placeholder {
    color: ${Core.text.subdued};
  }
  &:focus {
    outline: none;
    box-shadow: none;
    &::placeholder {
      color: ${Core.text.subdued};
    }
  }
  &:focus::placeholder {
    color: ${Core.text.subdued};
  }
`;

export const SearchInput = styled.input<ICustomAttrs>`
  ${({ $mini: mini }) => inputStyle(mini || false)}
  z-index: 1;
  background-color: transparent;
`;

export const IconWrapper = styled.section<ICustomAttrs>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $mini: mini }) => (mini ? "40px" : "51px")};
  height: 100%;
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  border-right: none;
`;

export const SearchResult = styled.ul`
  background: ${Core.surface.inverse};
  width: 100%;
  border: 1px solid ${Core.border.muted};
  border-top: none;
  z-index: 2;
  text-align: left;
  overflow-y: auto;
  max-height: ${SearchResultMaxHeight}px;
  li {
    border-bottom: 1px solid ${Core.border.muted};
    a {
      display: block;
      padding: ${getSpacing("3x", "4x")};
      &.search-item-selected,
      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
    h2 {
      ${TypographyStyles({})}
    }
    p {
      ${TypographyStyles({ color: Core.text.subdued })}
      em {
        font-weight: bold;
        font-style: normal;
      }
    }
  }
`;
